import type {
  CheckboxSelectionCallback,
  ICellRendererParams
} from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import type { ComponentType, RefObject } from 'react';

import type { LimaSourceAggregatedStatusModel } from '@/pages/Administration/Devices/SyncDevices/SyncDevices.types';
import type { components } from '@/types/openapi';

import type { CloudSourceTreeNode } from '../CloudSourcesTable/CloudSourcesTable.types';

// ============================================================================
// BASE TREE NODE TYPES
// ============================================================================

/**
 * Base tree node containing common fields shared across all AG Grid tree implementations
 */
export type BaseTreeNode = {
  id: string;
  path: string[];
  type: string;
  name: string;
  organization?: string;
  status?: string;
  statusLabel?: string;
  lastSeen?: string;
  hasChildren?: boolean;
  level?: number;
};

/**
 * Status priority mapping for severity-based sorting and filtering
 */
export const STATUS_PRIORITY: Record<string, number> = {
  critical: 4,
  warning: 3,
  info: 2,
  success: 1,
  unknown: 0
} as const;

// ============================================================================
// DEVICE TREE NODE TYPES
// ============================================================================

/**
 * Type of device tree node - device, source, or provider
 */
export type DeviceTreeNodeType = 'device' | 'source' | 'provider';

/**
 * Device tree node for AG Grid tree data
 * Combines device/source/provider data with tree structure
 */
export type DeviceTreeNode = BaseTreeNode & {
  type: DeviceTreeNodeType;
  deviceType?: string;
  ipAddress?: string | null;
  statusUpdatedAt?: string;
  sourceCount?: number;
  highestStatusCode?: number;
  sourceStatus?: string;
  actionType?: string;
  isSyncInProgress?: boolean;
  source_type_id?: number;

  // Original data references
  deviceData?: DeviceModel;
  sourceData?: SourceModel;
  providerData?: LimaSourceAggregatedStatusModel;
};

/**
 * Base cell renderer params extending AG Grid's ICellRendererParams
 */
export type DeviceCellRendererParams = ICellRendererParams & {
  data: DeviceTreeNode;
};

/**
 * Options for building device tree data
 */
export type DeviceTreeDataBuilderOptions = {
  selectedOrganizationId: number;
  includeMicrosoftProviders?: boolean;
};

/**
 * Tree level constants for device hierarchy
 */
export const DEVICE_TREE_LEVEL = {
  DEVICE: 0,
  SOURCE: 1,
  PROVIDER: 2
} as const;

// ============================================================================
// ACTION AND MENU TYPES
// ============================================================================

/**
 * Menu action types available for devices
 */
export type ActionMenuType =
  | 'edit'
  | 'delete'
  | 'addLogSources'
  | 'editLogSources';

/**
 * Configuration for row class styling
 */
export type RowClassConfig = {
  /**
   * Mapping of level numbers to CSS class names
   */
  levelClassMap: Record<number, string>;
  /**
   * CSS class for last child rows
   */
  lastChildClass: string;
  /**
   * CSS class for selected rows
   */
  selectedRowClass: string;
  /**
   * CSS class for detail pane open
   */
  detailOpenRowClass?: string;
};

export type SharedTreeGridConfig<T extends BaseTreeNode> = {
  /**
   * Field name displayed in the auto-group column
   */
  autoGroupField: string;
  /**
   * Header name for the auto-group column
   */
  autoGroupHeaderName: string;
  /**
   * Custom cell renderer for the inner content of group cells
   */
  innerRenderer: ComponentType<ICellRendererParams<T>>;
  /**
   * Function to determine if expand icon should be hidden for a node
   */
  suppressExpandIcon?: (params: ICellRendererParams<T>) => boolean;
  /**
   * Minimum width for the auto-group column
   */
  autoGroupMinWidth?: number;
  /**
   * Flex value for the auto-group column
   */
  autoGroupFlex?: number;
  /**
   * Enable checkbox selection in the auto-group column
   */
  checkboxSelection?: boolean | CheckboxSelectionCallback<T>;
  /**
   * Enable header checkbox for select all
   */
  headerCheckboxSelection?: boolean;
};

// ============================================================================
// BACKEND DTO TYPES (from OpenAPI schema)
// ============================================================================

export type DeviceDTO = components['schemas']['Device'];
export type LimaDeviceDetailsDTO =
  components['schemas']['LimaSourceAggregatedStatus'];
export type SyncInprogressDTO = components['schemas']['SyncInProgress'];
export type DevicesDeployedDetailsDTO =
  components['schemas']['DevicesDeployedDetails'];
export type SourceTypeDetailed = components['schemas']['SourceTypeDetailed'];
export type DetailedSourceDTO = components['schemas']['DetailedSource'];
export type DeviceStatusDTO = components['schemas']['DeviceStatus'];
export type LimaSourceStatusDTO = components['schemas']['LimaSourceStatus'];
export type LimaSourceStatusListDTO =
  components['schemas']['LimaSourceStatusList'];
export type ProcessStatusDTO = components['schemas']['SourceProcessBase'];
export type DeviceDetailsDTO = components['schemas']['DeviceDetails'];
export type LimaDetailedSourceDTO = components['schemas']['LimaDetailedSource'];
export type LogTypeStatusDataDTO = components['schemas']['LogTypeStatusData'];
export type LimaSourceEventsPerDayDTO =
  components['schemas']['LimaSourceEventsPerDay'];
export type LimaSourcesResponseDTO =
  components['schemas']['LimaSourcesResponse'];
export type LimaSourceActivationStatusDTO =
  components['schemas']['LimaSourceActivationStatus'];

// ============================================================================
// EXTENDED MODEL TYPES
// ============================================================================

/**
 * Extended LimaSources response with computed display fields
 */
export type LimaSourcesResponseModal = LimaSourcesResponseDTO & {
  status?: string;
  status_label?: string;
  last_seen_display?: string;
  category_failed_tooltip_text?: string | null;
  display_source_name?: string;
  org_label?: string;
  active_state?: string;
  is_selected?: boolean;
  device_name?: string;
  data_providers?: LimaSourcesResponseModal[];
  allowSourceActivation?: boolean;
};

/**
 * Extended LogTypeStatusData with event time
 */
export type LogTypeStatusDataModel = LogTypeStatusDataDTO & {
  event_time?: string;
};

/**
 * Extended DeviceDetails with aggregated status information
 */
export type DeviceDetailModel = DeviceDetailsDTO & {
  lima_source_aggregated_status?: LimaSourceAggregatedStatusModel[];
  lima_source_aggregated_status_display?:
    | LimaSourceAggregatedStatusModel[]
    | undefined;
};

/**
 * Extended DetailedSource with computed display fields
 */
export type SourceModel = DetailedSourceDTO & {
  last_seen_display?: string;
  status_updated_at_display?: string | undefined;
  status_label?: string | undefined;
  source_statuses?: LimaSourceStatusDTO[] | undefined;
  source_type?: SourceTypeDetailed | undefined;
};

/**
 * Extended Device model fields
 */
export type DeviceExtendModel = {
  last_seen_display: string | undefined;
  status_updated_at_display: string | undefined;
  source_count: number | undefined;
  source_status: string | undefined;
  status_label: string | undefined;
  isSyncInProgress?: boolean | undefined;
  status?: string | undefined;
  isDeleted?: boolean | undefined;
  actionType?: string | undefined;
  action?: string | undefined;
  lima_source_aggregated_status?: LimaSourceAggregatedStatusModel[] | undefined;
  lima_source_aggregated_status_display?: LimaSourceAggregatedStatusModel[];
  org_label?: string | undefined;
};

/**
 * Complete Device model with all extensions
 */
export type DeviceModel = DeviceDTO & DeviceExtendModel;

/**
 * Extended DetailedSource model with computed fields
 */
export type DetailedSourceModel = DetailedSourceDTO & {
  last_seen_display?: string;
  status_updated_at_display?: string | undefined;
  status_label?: string | undefined;
  source_statuses?: LimaSourceStatusDTO[] | undefined;
  source_type?: SourceTypeDetailed | undefined;
};

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Keys of DeviceModel for type-safe property access
 */
export type DevicesDTOKeys = keyof DeviceModel;

/**
 * Keys of LimaSourcesResponseModal for type-safe property access
 */
export type LimaSourcesDTOKeys = keyof LimaSourcesResponseModal;

/**
 * Grouped devices by device type
 */
export type GroupedDevices = {
  deviceType: string;
  devices: DeviceModel[];
};

/**
 * Device type definition
 */
export type DeviceType = {
  id: number;
  type: string;
};

/**
 * Device export data model for CSV/Excel export
 */
export type DeviceExportDataModel = {
  Description?: string;
  DeviceID?: string;
  DeviceSize: string;
  DeviceType: string;
  DisplayName: string;
  HostName: string;
  IpAddress: string;
  LastSeen: string;
  OrganizationCode: string;
  OrganizationName: string;
  SourceCount: number;
  Status: string;
  StatusUpdatedAt: string;
};

// ============================================================================
// COMPONENT PROP TYPES
// ============================================================================

/**
 * Callback function type with no parameters
 */
export type CallbackFunction = () => void;

/**
 * Callback function type with single string parameter
 */
export type CallbackFunctionWithParam = (param: string) => void;

/**
 * Callback function type with multiple string parameters
 */
export type CallbackFunctionWithMultiParam = (
  param1: string,
  param2: string
) => void;

/**
 * Props for DevicesTable component
 */
export type DevicesTableProps = {
  closeDeviceDetail: CallbackFunction;
  detailPanelVisible?: boolean;
  isMainDevicePage?: boolean;
};

/**
 * Props for DeviceTableHeader component
 */
export type DeviceTableHeaderProps = {
  refreshDevices?: CallbackFunction;
  gridRef?: RefObject<AgGridReact<DeviceTreeNode> | null>;
  collapsed?: boolean;
};

/**
 * Props for DeviceByDeviceType component
 */
export type DeviceByDeviceTypeProps = {
  row: GroupedDevices;
  isMainDevicePage: boolean;
};

// ============================================================================
// VALIDATION AND CONNECTION TYPES
// ============================================================================

/**
 * Validation rule definition
 */
export type Validation = {
  type: string;
  params: string[];
};

/**
 * Connection input field configuration
 */
export type ConnectionInput = {
  id: number;
  field_name: string;
  input_type: string;
  label: string;
  value: string;
  disabled: boolean;
  validations: Validation[];
  validation_type: string;
  options?: string[];
  retention?: number;
  msg?: string;
  nonMandatory?: boolean;
  min?: number;
  category?: string[];
};

export type CloudSourceTableHeaderProps = {
  refreshSources: CallbackFunction;
  gridRef: RefObject<AgGridReact<CloudSourceTreeNode> | null>;
  collapsed?: boolean;
  isCustomerCloudSourcePage?: boolean;
};
